import React, { useEffect, useState } from "react";
import { MessagesSquare, Plus, Trash2, Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import LucideIconByName from "@/components/LucideIconByName";

const ICON_OPTIONS = ["MessageSquare", "Star", "Tv", "Zap", "Film", "Flame", "Trophy", "Heart", "Crown", "Globe", "Sparkles"];

export default function AdminForums() {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: "", description: "", icon: "MessageSquare", color: "#FF2A6D", sort_order: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forums");
      setForums(Array.isArray(data) ? data : []);
    } catch { setForums([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/forums", draft);
      toast.success(`Forum created: ${draft.name}`);
      setDraft({ name: "", description: "", icon: "MessageSquare", color: "#FF2A6D", sort_order: 0 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const remove = async (forum_id, name) => {
    if (!window.confirm(`Delete "${name}" and all its threads/posts?`)) return;
    try { await api.delete(`/admin/forums/${forum_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <section className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <MessagesSquare className="w-4 h-4 text-[var(--tt-primary)]" />
        <h3 className="font-display font-bold text-sm">Forums</h3>
      </div>

      <form onSubmit={create} data-testid="admin-forum-create-form" className="grid grid-cols-12 gap-2 mb-5 items-end">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          placeholder="Name"
          required
          data-testid="admin-forum-name"
          className="col-span-12 sm:col-span-3 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
        />
        <input
          type="text"
          value={draft.description}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          placeholder="Description"
          data-testid="admin-forum-description"
          className="col-span-12 sm:col-span-4 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
        />
        <select
          value={draft.icon}
          onChange={(e) => setDraft((d) => ({ ...d, icon: e.target.value }))}
          data-testid="admin-forum-icon"
          className="col-span-6 sm:col-span-2 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-2 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
        >
          {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input
          type="color"
          value={draft.color}
          onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
          data-testid="admin-forum-color"
          className="col-span-3 sm:col-span-1 h-10 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg cursor-pointer"
        />
        <input
          type="number"
          value={draft.sort_order}
          onChange={(e) => setDraft((d) => ({ ...d, sort_order: parseInt(e.target.value || "0", 10) }))}
          placeholder="Order"
          data-testid="admin-forum-sort"
          className="col-span-3 sm:col-span-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-forum-add-btn"
          className="col-span-12 sm:col-span-1 inline-flex items-center justify-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add
        </button>
      </form>

      {loading ? (
        <div className="text-xs text-[var(--tt-text-muted)] py-4 text-center">Loading...</div>
      ) : forums.length === 0 ? (
        <div className="text-xs text-[var(--tt-text-muted)] py-4 text-center">No forums yet.</div>
      ) : (
        <ul className="space-y-2">
          {forums.map((f) => (
            <li
              key={f.forum_id}
              data-testid={`admin-forum-row-${f.slug}`}
              className="flex items-center gap-3 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2"
            >
              <div
                className="w-8 h-8 rounded flex items-center justify-center shrink-0"
                style={{ background: `${f.color}22`, border: `1px solid ${f.color}55` }}
              >
                <LucideIconByName name={f.icon} className="w-4 h-4" color={f.color} strokeWidth={2.4} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm truncate">{f.name}</div>
                <div className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt">/{f.slug} · {f.thread_count} threads · {f.post_count} posts</div>
              </div>
              <button
                onClick={() => remove(f.forum_id, f.name)}
                data-testid={`admin-forum-delete-${f.slug}`}
                className="text-[10px] uppercase tracking-wider font-bold text-[var(--tt-text-muted)] hover:text-red-400 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
